import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasFeature } from "../lib/features";
import { Badge } from "./ui/badge";
import { CalendarCheck, ArrowRight, Lock, ShieldCheck, Truck } from "@phosphor-icons/react";

const fmt = (d) => new Date(d + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short" });
const when = (i) => (i.state === "overdue" ? `${i.days} day${i.days === 1 ? "" : "s"} overdue` : i.days === 0 ? "Due today" : `in ${i.days} day${i.days === 1 ? "" : "s"}`);

function Row({ item, primary, secondary }) {
  return (
    <Link to={item.path} className="flex items-center justify-between gap-3 py-1.5 px-2 rounded-md hover:bg-muted/60 transition-colors" data-testid={`expiring-row-${item.id}`}>
      <div className="min-w-0">
        <p className="text-xs font-medium truncate">{primary}</p>
        <p className="text-[10px] text-muted-foreground truncate">{secondary}</p>
      </div>
      <div className="text-right shrink-0">
        <p className={`text-[11px] font-semibold ${item.state === "overdue" ? "text-red-600" : "text-amber-600"}`}>{when(item)}</p>
        <p className="text-[10px] text-muted-foreground">{fmt(item.date)}</p>
      </div>
    </Link>
  );
}

function Group({ icon: Icon, title, data, render, emptyText, footer, testid }) {
  return (
    <div className="rounded-md border border-border p-3" data-testid={testid}>
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-xs font-semibold flex items-center gap-1.5"><Icon size={13} /> {title}</p>
        <div className="flex gap-1">
          {data.overdue > 0 && <Badge className="bg-red-100 text-red-700 hover:bg-red-100 text-[10px]" data-testid={`${testid}-overdue`}>{data.overdue} overdue</Badge>}
          {data.due > 0 && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-[10px]" data-testid={`${testid}-due`}>{data.due} due</Badge>}
        </div>
      </div>
      {data.items.length === 0 ? <p className="text-[11px] text-muted-foreground py-2">{emptyText}</p> : data.items.slice(0, 5).map(render)}
      {footer}
    </div>
  );
}

export default function ExpiringDocumentsWidget() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => { API.get("/dashboard/expiring-documents").then(({ data }) => setData(data)).catch(() => setData(false)); }, []);
  if (data === false) return null;
  const hubLocked = data?.hub?.locked || !hasFeature(user, "compliance_hub");
  const total = (data?.hub?.total || 0) + (data?.suppliers?.total || 0);

  return (
    <div className="border border-border rounded-md p-4 animate-fade-in" data-testid="expiring-documents-widget">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold flex items-center gap-2"><CalendarCheck size={16} className="text-amber-600" /> Expiring this month <span className="text-muted-foreground font-normal">· {data?.month || ""}</span></h3>
          <p className="text-[11px] text-muted-foreground">Compliance reviews and supplier certificates due by month end{total ? ` — ${total} to action` : ""}</p>
        </div>
        {!hubLocked && <Link to="/compliance-hub" className="text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid="expiring-view-all">Compliance Hub <ArrowRight size={12} /></Link>}
      </div>
      {!data ? <p className="text-xs text-muted-foreground">Loading…</p> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {hubLocked ? (
            <div className="rounded-md border border-dashed border-border p-3 flex items-start gap-2" data-testid="expiring-hub-locked">
              <Lock size={14} className="text-muted-foreground mt-0.5" />
              <div>
                <p className="text-xs font-semibold">Compliance Hub reviews</p>
                <p className="text-[11px] text-muted-foreground">Track your own policies, RAMS and certificates with review dates. <Link to="/platform/billing?add=compliance_hub" className="text-primary hover:underline" data-testid="expiring-hub-add-link">Add Compliance Hub</Link></p>
              </div>
            </div>
          ) : (
            <Group icon={ShieldCheck} title="Compliance Hub reviews" data={data.hub} testid="expiring-hub" emptyText="No reviews due this month."
              render={(i) => <Row key={i.id} item={i} primary={i.title} secondary={[i.category, i.owner_name].filter(Boolean).join(" · ")} />}
              footer={data.hub.total > 5 && <Link to="/compliance-hub" className="block text-[11px] text-primary hover:underline pt-1 px-2">+{data.hub.total - 5} more in the Compliance Hub</Link>} />
          )}
          <Group icon={Truck} title="Supplier certificates" data={data.suppliers} testid="expiring-suppliers" emptyText="No supplier documents expiring this month."
            render={(i) => <Row key={i.id} item={i} primary={i.vendor_name} secondary={i.doc_type} />}
            footer={data.suppliers.total > 5 && <Link to="/supplier-compliance" className="block text-[11px] text-primary hover:underline pt-1 px-2">+{data.suppliers.total - 5} more in supplier compliance</Link>} />
        </div>
      )}
    </div>
  );
}
